// Display-only conversion. Never use this helper to serialize requests or sync cursors.
export const ADMIN_TIME_ZONE = "Asia/Riyadh";
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i;

// Verified local backend contracts, not a default for arbitrary unzoned values.
// AssetDashboardController::collectorProgress, User::attributesToArray,
// Product::attributesToArray explicitly parse these fields using config(app.timezone).
// The configured backend uses Asia/Karachi. Reconfirm these rules if that changes.
export const API_TIMESTAMP_SOURCE_ZONES = Object.freeze({
    "dashboard.collector.last_sync_at": "Asia/Karachi",
    "user.date_added": "Asia/Karachi",
    "user.date_modified": "Asia/Karachi",
    "product.date_added": "Asia/Karachi",
});

function sourceInstant(text, zone) {
    const sourceFormatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: zone, timeZoneName: "longOffset", hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = (date) => Object.fromEntries(sourceFormatter.formatToParts(date).map(({ type, value }) => [type, value]));
    const wallTime = text.replace(" ", "T");
    const provisional = new Date(`${wallTime}Z`);
    const offset = parts(provisional).timeZoneName.replace("GMT", "") || "Z";
    const instant = new Date(`${wallTime}${offset}`);
    if (Number.isNaN(instant.getTime())) return null;
    const actual = parts(instant);
    const expected = TIMESTAMP.exec(text);
    if (`${actual.year}-${actual.month}-${actual.day}` !== expected[1] || actual.hour !== expected[2] || actual.minute !== expected[3] || actual.second !== (expected[4] || "00")) return null;
    // Do not guess at historical clock transitions with ambiguous wall times.
    const day = 24 * 60 * 60 * 1000;
    if (parts(new Date(instant.getTime() - day)).timeZoneName !== parts(new Date(instant.getTime() + day)).timeZoneName) return null;
    return instant;
}

function validDay(value) {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function inspectTimestamp(value, field) {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? {} : { instant: value };
    if (typeof value !== "string") return {};
    const text = value.trim();
    if (DATE_ONLY.test(text)) return validDay(text) ? { dateOnly: text } : {};
    const match = TIMESTAMP.exec(text);
    if (!match || !validDay(match[1]) || Number(match[2]) > 23 || Number(match[3]) > 59 || Number(match[4] || 0) > 59) return {};
    // A wall-clock timestamp without an offset does not identify an instant.
    if (!match[5]) {
        const zone = API_TIMESTAMP_SOURCE_ZONES[field];
        const instant = zone ? sourceInstant(text, zone) : null;
        return instant ? { instant } : { unspecified: text };
    }
    const instant = new Date(text.replace(" ", "T"));
    return Number.isNaN(instant.getTime()) ? {} : { instant };
}

export function parseApiTimestamp(value, field) {
    return inspectTimestamp(value, field).instant || null;
}

const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: ADMIN_TIME_ZONE,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
});

export function formatTimestamp(value, pattern = "yyyy-MM-dd HH:mm:ss", empty = "—", field) {
    const { instant, dateOnly, unspecified } = inspectTimestamp(value, field);
    if (dateOnly) return dateOnly;
    if (unspecified) return `${unspecified} (timezone unspecified)`;
    if (!instant) return empty;
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value: part }) => [type, part]));
    const hour = Number(parts.hour);
    const tokens = {
        yyyy: parts.year, MM: parts.month, dd: parts.day, HH: parts.hour,
        hh: String(hour % 12 || 12).padStart(2, "0"), mm: parts.minute, ss: parts.second,
        a: hour >= 12 ? "PM" : "AM",
    };
    return pattern.replace(/yyyy|MM|dd|HH|hh|mm|ss|a/g, (token) => tokens[token]);
}

// Display-derived day only; request/filter date strings must remain unchanged.
export function getSaudiDate(value, field) {
    const { instant, dateOnly } = inspectTimestamp(value, field);
    return dateOnly || (instant ? formatTimestamp(instant, "yyyy-MM-dd") : null);
}
